import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  LocalPharmacy,
  DeliveryDining,
  Upload,
  Phone,
  LocationOn,
  CheckCircle,
  Pending,
  LocalShipping
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../../services/orderService';

const OrdersPage = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getMyOrders();
      setOrders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'preparing': return 'info';
      case 'in-transit': return 'primary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Pending />;
      case 'preparing': return <LocalPharmacy />;
      case 'in-transit': return <LocalShipping />;
      case 'delivered': return <CheckCircle />;
      default: return <Pending />;
    }
  };

  const getOrderSteps = (status) => {
    const steps = ['Order Placed', 'Confirmed', 'Preparing', 'Ready', 'In Transit', 'Delivered'];
    let activeStep = 0;
    
    switch (status) {
      case 'pending': activeStep = 0; break;
      case 'confirmed': activeStep = 1; break;
      case 'preparing': activeStep = 2; break;
      case 'ready': activeStep = 3; break;
      case 'in-transit': activeStep = 4; break;
      case 'delivered': activeStep = 5; break;
      default: activeStep = 0;
    }
    
    return { steps, activeStep };
  };

  const handleUploadReceipt = (order) => {
    setSelectedOrder(order);
    setUploadDialogOpen(true);
  };

  const handleTrackOrder = (order) => {
    setSelectedOrder(order);
    setTrackingDialogOpen(true);
  };

  const handleUploadFile = () => {
    // File upload logic would go here
    console.log('Uploading receipt for order:', selectedOrder?.orderNumber);
    setUploadDialogOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/customer/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
            My Orders
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              No orders yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Start shopping to see your orders here
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
            >
              Browse Products
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {orders.map((order) => (
              <Grid size={{ xs: 12 }} key={order._id || order.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Order #{order.orderNumber || order._id?.slice(-8)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Placed on {new Date(order.createdAt || order.orderDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={order.status.replace('-', ' ').toUpperCase()}
                          color={getStatusColor(order.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Chip
                          label={`Payment: ${order.paymentStatus || 'pending'}`}
                          color={getPaymentStatusColor(order.paymentStatus)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={3}>
                      {/* Order Details */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                          Order Details
                        </Typography>
                        
                        <List dense>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <LocalPharmacy />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={order.pharmacy}
                              secondary={
                                <Box>
                                  <Button size="small" startIcon={<Phone />} sx={{ mr: 1 }}>
                                    Call
                                  </Button>
                                </Box>
                              }
                            />
                          </ListItem>
                          
                          {order.rider && (
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                  <DeliveryDining />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={order.rider.name}
                                secondary={
                                  <Button size="small" startIcon={<Phone />}>
                                    {order.rider.phone}
                                  </Button>
                                }
                              />
                            </ListItem>
                          )}
                          
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'info.main' }}>
                                <LocationOn />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary="Delivery Address"
                              secondary={
                                order.deliveryAddress ? 
                                  `${order.deliveryAddress.street || ''}, ${order.deliveryAddress.city || ''}, ${order.deliveryAddress.state || ''}, ${order.deliveryAddress.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') :
                                  'No address provided'
                              }
                            />
                          </ListItem>
                        </List>

                        <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                          Items Ordered
                        </Typography>
                        {order.items.map((item, index) => (
                          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {item.name} x {item.quantity}
                            </Typography>
                            <Typography variant="body2">
                              ₦{(item.price * item.quantity).toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                        
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            Total Amount
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {order.currency === 'NGN' ? '₦' : '$'}{order.totalAmount?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Payment Method:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {order.paymentMethod === 'card' ? 'Card (Paystack)' : 
                             order.paymentMethod === 'bank-transfer' ? 'Bank Transfer' :
                             order.paymentMethod || 'Not specified'}
                          </Typography>
                        </Box>
                        {order.paymentReference && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Payment Reference:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {order.paymentReference}
                            </Typography>
                          </Box>
                        )}
                        {order.paidAt && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Paid At:
                            </Typography>
                            <Typography variant="body2">
                              {new Date(order.paidAt).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Grid>

                      {/* Order Actions */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                          Order Status
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Stepper activeStep={getOrderSteps(order.status).activeStep} orientation="vertical">
                            {getOrderSteps(order.status).steps.map((label, index) => (
                              <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                              </Step>
                            ))}
                          </Stepper>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleTrackOrder(order)}
                            startIcon={<LocationOn />}
                          >
                            Track Order
                          </Button>
                          
                          {order.paymentStatus === 'pending' && (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => handleUploadReceipt(order)}
                              startIcon={<Upload />}
                            >
                              Upload Payment Receipt
                            </Button>
                          )}
                          
                          {order.status === 'delivered' && (
                            <Button
                              variant="outlined"
                              fullWidth
                              color="success"
                            >
                              Reorder
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Upload Receipt Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Payment Receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Order #{selectedOrder?.orderNumber} - ₦{selectedOrder?.totalAmount?.toLocaleString()}
          </Typography>
          <TextField
            fullWidth
            type="file"
            margin="normal"
            inputProps={{ accept: 'image/*,.pdf' }}
          />
          <Typography variant="caption" color="text.secondary">
            Upload a clear photo or PDF of your payment receipt
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUploadFile} variant="contained">
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Track Order Dialog */}
      <Dialog open={trackingDialogOpen} onClose={() => setTrackingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Track Order #{selectedOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Current Status: {selectedOrder.status.replace('-', ' ').toUpperCase()}
              </Typography>
              
              {selectedOrder.rider && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Delivery Rider
                  </Typography>
                  <Typography variant="body2">
                    {selectedOrder.rider.name} - {selectedOrder.rider.phone}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Delivery Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(getOrderSteps(selectedOrder.status).activeStep / 5) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersPage;
